import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { Collapse, Nav } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { Route } from 'sitemap';
import { capitalize } from 'helpers/utils';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { useNavbarVerticalCollapse } from './NavbarVerticalCollapseProvider';
import Badge from 'components/base/Badge';
import { useAppContext } from 'providers/AppProvider';
import { AwaitingProcessingCounts } from 'hooks/useAwaitingProcessingCounts';

interface NavbarVerticalMenuProps {
  routes: Route[];
  level: number;
  awaitingCounts?: AwaitingProcessingCounts;
}

interface NavItemProps {
  route: Route;
  level: number;
  isLinkActive?: boolean;
  awaitingCounts?: AwaitingProcessingCounts;
}

const getRouteAwaitingCount = (
  route: Route,
  counts?: AwaitingProcessingCounts
): number => {
  if (!counts) return 0;
  if (route.awaitingCountKey) {
    return counts[route.awaitingCountKey] ?? 0;
  }
  if (!route.pages?.length) return 0;
  return route.pages.reduce(
    (sum, child) => sum + getRouteAwaitingCount(child, counts),
    0
  );
};

const formatAwaitingCount = (count: number): string =>
  count > 99 ? '99+' : String(count);

const AwaitingCountBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null;
  return (
    <Badge bg="danger" className="ms-2" pill>
      {formatAwaitingCount(count)}
    </Badge>
  );
};

const NavItem = ({ route, level, awaitingCounts }: NavItemProps) => {
  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();
  const { setOpenItems, openItems } = useNavbarVerticalCollapse();
  const { pathname } = useLocation();

  const isActive = (path: string | undefined, currentPath: string): boolean => {
    if (!path) return false;
    // Check if the current path matches the route path exactly or is a subpath of it
    return currentPath === path || currentPath.includes(`${path}/`);
  };

  return (
    <Nav.Item as="li">
      <NavLink
        to={route.path ? route.path : '#!'}
        className={() =>
          classNames('nav-link', {
            'label-1': level === 1,
            active: isActive(route.path, pathname)
          })
        }
        onClick={() => level === 1 && setOpenItems(openItems.map(() => ''))}
      >
        <div
          className={classNames('d-flex align-items-center', {
            'text-light': !route.active
          })}
        >
          {route.icon ? (
            <>
              <span
                className={classNames('nav-link-icon', {
                  new: route.new || route.hasNew
                })}
              >
                {route.iconSet === 'font-awesome' ? (
                  <FontAwesomeIcon
                    icon={route.icon as IconProp}
                    transform={{ size: 16 }}
                  />
                ) : (
                  <FeatherIcon icon={route.icon} size={16} />
                )}
              </span>
              <span className="nav-link-text-wrapper">
                <span className="nav-link-text">{capitalize(route.name)}</span>
                {route.new && !isNavbarVerticalCollapsed && (
                  <Badge variant="phoenix" bg="info" className="ms-2">
                    New
                  </Badge>
                )}
                {!isNavbarVerticalCollapsed && (
                  <AwaitingCountBadge
                    count={getRouteAwaitingCount(route, awaitingCounts)}
                  />
                )}
              </span>
            </>
          ) : (
            <>
              <span className="nav-link-text">{capitalize(route.name)}</span>
              {route.new && (
                <Badge variant="phoenix" bg="info" className="ms-2">
                  New
                </Badge>
              )}
              <AwaitingCountBadge
                count={getRouteAwaitingCount(route, awaitingCounts)}
              />
            </>
          )}
        </div>
      </NavLink>
    </Nav.Item>
  );
};

const CollapsibleNavItem = ({ route, level, awaitingCounts }: NavItemProps) => {
  const { pathname } = useLocation();

  const { setOpenItems, openItems } = useNavbarVerticalCollapse();
  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();

  const openCollapse = (childrens: Route[] = []) => {
    const checkLink = (children: Route) => {
      if (`${children.path}` === pathname) {
        return true;
      }
      return children.pages && children.pages.some(checkLink);
    };
    return childrens.some(checkLink);
  };

  const updateOpenItems = (name: string) => {
    const updatedOpenItems = [...openItems];
    updatedOpenItems[level] = name;
    updatedOpenItems.forEach((item, index) => {
      if (index > level) {
        updatedOpenItems[index] = '';
      }
    });
    setOpenItems(updatedOpenItems);
  };

  useEffect(() => {
    if (openCollapse(route.pages)) {
      updateOpenItems(route.name);
    }
  }, []);

  return (
    <>
      <Nav.Link
        onClick={() => {
          if (route.name === openItems[level]) {
            updateOpenItems('');
          } else {
            updateOpenItems(route.name);
          }
        }}
        className={classNames('dropdown-indicator', {
          'label-1': level === 1,
          collapsed: openItems[level] !== route.name,
          'text-light': !route.active
        })}
        aria-expanded={openItems[level] === route.name}
      >
        <div className="d-flex align-items-center">
          <div className="dropdown-indicator-icon">
            <FontAwesomeIcon
              icon={faCaretRight}
              className={classNames({
                'text-light': !route.active
              })}
            />
          </div>
          {level === 1 && (
            <span
              className={classNames('nav-link-icon', {
                new: route.new || route.hasNew
              })}
            >
              <FeatherIcon icon={route.icon} size={16} />
            </span>
          )}
          <span
            className={classNames('nav-link-text', {
              new: route.hasNew
            })}
          >
            {capitalize(route.name)}
            {(!isNavbarVerticalCollapsed || level !== 1) && route.new && (
              <Badge variant="phoenix" bg="info" className="ms-2">
                New
              </Badge>
            )}
            {(!isNavbarVerticalCollapsed || level !== 1) && (
              <AwaitingCountBadge
                count={getRouteAwaitingCount(route, awaitingCounts)}
              />
            )}
          </span>
        </div>
      </Nav.Link>
      <div
        className={classNames('parent-wrapper', {
          'label-1': level === 1
        })}
      >
        <Collapse in={openItems[level] === route.name} className="nav parent">
          <div>
            {level === 1 && (
              <div className="collapsed-nav-item-title d-none">
                {capitalize(route.name)}
                {isNavbarVerticalCollapsed && route.new && (
                  <Badge variant="phoenix" bg="info" className="ms-2">
                    New
                  </Badge>
                )}
              </div>
            )}
            <NavbarVerticalMenu
              routes={route.pages || []}
              level={level + 1}
              awaitingCounts={awaitingCounts}
            />
          </div>
        </Collapse>
      </div>
    </>
  );
};

const NavbarVerticalMenu = ({
  routes,
  level,
  awaitingCounts
}: NavbarVerticalMenuProps) => {
  return (
    <>
      {routes.map(route => (
        <div key={route.name}>
          {level === 1 ? (
            <div className="nav-item-wrapper">
              {route.pages ? (
                <CollapsibleNavItem
                  route={route}
                  level={level}
                  awaitingCounts={awaitingCounts}
                />
              ) : (
                <NavItem
                  route={route}
                  level={level}
                  awaitingCounts={awaitingCounts}
                />
              )}
            </div>
          ) : (
            <>
              {route.pages ? (
                <CollapsibleNavItem
                  route={route}
                  level={level}
                  awaitingCounts={awaitingCounts}
                />
              ) : (
                <NavItem
                  route={route}
                  level={level}
                  awaitingCounts={awaitingCounts}
                />
              )}
            </>
          )}
        </div>
      ))}
    </>
  );
};

export default NavbarVerticalMenu;
